import {Trans} from '@lingui/macro';
import React, {FC, SyntheticEvent, useCallback, useRef} from 'react';
import styled, {css} from 'styled-components';

import DragAndDropImgSrc from '../../../../../../../../src/assets/images/dragAndDrop.svg';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

export interface DragAndDropInnerV1Props {
  hasError?: boolean;
  isFileHovering: boolean;
  isUploading?: boolean;
  inputKey: number;
  onChange: HandlerOf<SyntheticEvent<HTMLInputElement>>;
}

/*
 * Style.
 */

interface StyledWrapperDivProps {
  $isFileHovering: boolean;
  $isUploading: boolean;
  $hasError: boolean;
}

const StyledWrapperDiv = styled.div<StyledWrapperDivProps>`
  display: flex;
  flex-flow: row;
  align-items: center;
  border-radius: 16px;
  border: 2px dashed ${(p) => p.theme.greys.shade40};
  padding: 26px;

  ${(p) =>
    (p.$isUploading || p.$hasError) &&
    css`
      margin-bottom: 20px;
    `}

  ${(p) =>
    p.$isFileHovering &&
    css`
      border-color: ${p.theme.palette.blue.shade40};
      background: ${p.theme.palette.blue.shade10};
    `}
`;

interface StyledDragAndDropTextDivProps {
  $isUploading: boolean;
}
const StyledDragAndDropTextDiv = styled.div<StyledDragAndDropTextDivProps>`
  font-weight: ${fontWeights.semibold};
  margin-left: 26px;
  margin-right: 8px;

  ${(p) =>
    p.$isUploading &&
    css`
      color: ${p.theme.greys.shade60};
    `}
`;

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

/*
 * Component.
 */

export const DragAndDropInnerV1: FC<DragAndDropInnerV1Props> = ({
  hasError,
  isFileHovering,
  isUploading,
  inputKey,
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onAddFileClick = useCallback(() => {
    if (!fileInputRef.current || isUploading) {
      return;
    }
    fileInputRef.current.click();
  }, [isUploading]);

  return (
    <StyledWrapperDiv
      $isFileHovering={isFileHovering}
      $isUploading={Boolean(isUploading)}
      $hasError={Boolean(hasError)}
    >
      <img src={DragAndDropImgSrc} alt="drag-drop-logo" />
      <StyledDragAndDropTextDiv $isUploading={Boolean(isUploading)}>
        <Trans id="accounts-manager-drag-and-drop-text">Drag & Drop, or </Trans>
      </StyledDragAndDropTextDiv>
      <Button onClick={onAddFileClick} variant={ButtonVariantsEnum.SECONDARY} isDisabled={isUploading}>
        <Trans id="accounts-manager-drag-and-drop-choose-file-button">Choose file</Trans>
      </Button>
      <StyledFileLabel>
        <input ref={fileInputRef} key={inputKey} type="file" accept="*" onChange={onChange} />
      </StyledFileLabel>
    </StyledWrapperDiv>
  );
};
